/* gr-pdp.js — PDP interactions.
 * - qty +/- buttons
 * - variant option selection via chip clicks (syncs hidden id input)
 * - price + subscribe box update on variant change
 * - thumbnail → main image swap
 */

(() => {
  const root = document.querySelector('.gr-pdp');
  if (!root) return;

  // ── Money formatter (fallback if Shopify.formatMoney unavailable) ──
  function fmt(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Quantity +/- ──────────────────────────────────────────────────────
  root.querySelectorAll('[data-qty-delta]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = root.querySelector('.gr-pdp__qty-num');
      const delta = parseInt(btn.dataset.qtyDelta, 10);
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  // ── Option chips → variant id ────────────────────────────────────────
  const form = root.querySelector('#gr-pdp-form');
  const idInput = form?.querySelector('input[name="id"]');
  const addBtn = form?.querySelector('.gr-pdp__add');
  const qtyInput = root.querySelector('.gr-pdp__qty-num');
  const stickyBtn = document.querySelector('.gr-sticky-atc__btn');
  const soldOutLabel = root.dataset.labelSoldOut || 'SOLD OUT';
  const addToCartLabel = root.dataset.labelAddToCart || 'ADD TO CART';
  const selected = {};
  root.querySelectorAll('.gr-pdp__option-chips').forEach((row) => {
    const idx = parseInt(row.dataset.optionIndex, 10);
    const active = row.querySelector('.is-active');
    if (active) selected[idx] = active.dataset.optionValue;
    row.querySelectorAll('.gr-pdp__chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        row.querySelectorAll('.gr-pdp__chip').forEach((c) => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
        selected[idx] = chip.dataset.optionValue;
        syncVariant();
        refreshChipAvailability();
      });
    });
  });

  // ── Sold-out state ────────────────────────────────────────────────────
  function setBuyState(variant) {
    const available = variant.available;
    if (addBtn) {
      addBtn.disabled = !available;
      addBtn.classList.toggle('gr-pdp__add--sold-out', !available);
      addBtn.setAttribute('aria-disabled', String(!available));
      if (available) {
        if (!addBtn.querySelector('.gr-pdp__add-price')) {
          addBtn.textContent = addToCartLabel + ' ';
          const priceSpan = document.createElement('span');
          priceSpan.className = 'gr-pdp__add-price';
          addBtn.appendChild(priceSpan);
        }
      } else {
        addBtn.textContent = soldOutLabel;
      }
    }
    if (qtyInput) qtyInput.disabled = !available;
    if (stickyBtn) {
      stickyBtn.disabled = !available;
      stickyBtn.classList.toggle('gr-sticky-atc__btn--sold-out', !available);
      stickyBtn.setAttribute('aria-disabled', String(!available));
      stickyBtn.textContent = available ? addToCartLabel : soldOutLabel;
    }
  }

  // A chip is sold out when no available variant matches it combined with
  // the currently selected values of the other options.
  function refreshChipAvailability() {
    const product = window.__productJson;
    if (!product) return;
    root.querySelectorAll('.gr-pdp__option-chips').forEach((row) => {
      const idx = parseInt(row.dataset.optionIndex, 10);
      row.querySelectorAll('.gr-pdp__chip').forEach((chip) => {
        const value = chip.dataset.optionValue;
        const anyAvailable = product.variants.some((v) =>
          v.available && v.options.every((o, i) => {
            if (i === idx) return o === value;
            return selected[i] == null || o === selected[i];
          })
        );
        chip.classList.toggle('is-soldout', !anyAvailable);
      });
    });
  }
  refreshChipAvailability();

  function syncVariant() {
    const product = window.__productJson;
    if (!product || !idInput) return;
    const opts = Object.keys(selected).sort().map((k) => selected[k]);
    const variant = product.variants.find((v) =>
      v.options.every((o, i) => o === opts[i])
    );
    if (!variant) return;

    idInput.value = variant.id;

    setBuyState(variant);

    // Update main price
    const priceEl = root.querySelector('.gr-pdp__price-now');
    if (priceEl) priceEl.textContent = fmt(variant.price);

    // Update add-to-cart price
    const addPrice = root.querySelector('.gr-pdp__add-price');

    // Update subscribe box prices
    const subBox = root.querySelector('[data-gr-sub]');
    const subToggle = subBox ? subBox.querySelector('[data-gr-sub-toggle]') : null;
    const isSubscribed = subToggle && subToggle.checked;

    if (subBox) {
      // Calculate subscription price (15% off)
      const discountPct = 15;
      const savingsCents = Math.round(variant.price * discountPct / 100);
      const subPrice = variant.price - savingsCents;

      const subNew = subBox.querySelector('.gr-sub__savings-new');
      const subOld = subBox.querySelector('.gr-sub__savings-old');
      if (subNew) subNew.textContent = fmt(subPrice);
      if (subOld) subOld.textContent = fmt(variant.price);

      if (isSubscribed) {
        if (addPrice) addPrice.textContent = fmt(subPrice);
      } else {
        if (addPrice) addPrice.textContent = fmt(variant.price);
      }
    } else {
      if (addPrice) addPrice.textContent = fmt(variant.price);
    }

    // Update compare-at price
    const compareEl = root.querySelector('.gr-pdp__price-was');
    const sepEl = root.querySelector('.gr-pdp__price-sep');
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) { compareEl.textContent = fmt(variant.compare_at_price); compareEl.hidden = false; }
      if (sepEl) sepEl.hidden = false;
    } else {
      if (compareEl) compareEl.hidden = true;
      if (sepEl) sepEl.hidden = true;
    }

    // Update variant image
    if (variant.featured_image) {
      const mainImg = root.querySelector('.gr-pdp__main-img');
      if (mainImg) mainImg.src = variant.featured_image.src;
    }
  }

  // ── Thumbnails ────────────────────────────────────────────────────────
  root.querySelectorAll('.gr-pdp__thumb').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      root.querySelectorAll('.gr-pdp__thumb').forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-pressed', 'true');
    });
  });
})();
